import { Box, Chip, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import React, { useContext, useState } from "react";

import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { fleetGroupQuery, fleetGroupWithItemsQuery, removeFleetGroupById } from "@/models/repositories/FleetGroup.ts";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider.tsx";
import GenericToggleGroup from "./GenericToggleGroup";

const filterArray = (originalArray: string[], newArray: string[]) => {
  const hasSome = newArray.some((feedId) => originalArray?.includes(feedId));
  if (hasSome) {
    return originalArray?.filter((i) => !newArray.includes(i));
  }
  return [...originalArray, ...newArray];
};

export default function MyGroups({
  activeChannels,
  mutateKey,
  setActiveIds,
}: {
  activeChannels: string[];
  setActiveIds: (value: string[]) => void;
  mutateKey: (value: string) => void;
}) {
  const { currentWorkspaceId: workspaceId, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const theme = useTheme();
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [dynamicKey, setDynamicKey] = useState<string | null>(null);
  const [disabled, setDisabled] = useState<boolean>(false);

  const mutatActiveChannels = (id: string) => {
    const channels = activeChannels?.includes(id)
      ? activeChannels?.filter((feedId) => feedId !== id)
      : [...activeChannels, id];
    setActiveIds(channels);
  };

  const toggleMassUpdate = (values: string[]) => {
    setActiveIds(filterArray(activeChannels, values));
  };

  const deselectCertainIds = (values: string[]) => {
    const filtered = activeChannels?.filter((feedId) => !values.includes(feedId));
    setActiveIds(filtered);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) {
      return;
    }
    setActiveIds([]);
    setDisabled(() => true);
    await removeFleetGroupById(confirmDelete);
    setDynamicKey(() => new Date().toISOString());
    mutateKey(new Date().toISOString());
    setConfirmDelete(() => null);
    setDisabled(() => false);
  };

  const fleetGroups = useReactiveSelectQuery(fleetGroupQuery, [{ workspaceId }], "MyGroups:fleetGroupQuery", 10) ?? [];

  const fleetGroupItems =
    useReactiveSelectQuery(
      fleetGroupWithItemsQuery,
      [
        {
          ids: fleetGroups?.map((group) => group.id),
          workspaceMembershipId: myCurrentWorkspaceMembership?.id,
        },
      ],
      "MyGroups:fleetGroupWithItemsQuery",
    ) ?? [];

  const feedGroupsWithItems = fleetGroups?.map((group) => ({
    ...group,
    fleetGroupItems: fleetGroupItems.filter((fgi) => fgi.fleetGroupId === group.id),
  }));

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          height: "365px",
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        {confirmDelete ? (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              background: theme.palette.secondary.dark,
              zIndex: 10,
            }}
          >
            <Box>
              <Box>Are you sure you want to delete this group?</Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <Box>
                  <Button disabled={disabled} onClick={() => setConfirmDelete(() => null)} variant="outlined">
                    Cancel
                  </Button>
                </Box>
                <Box>
                  <Button disabled={disabled} variant="contained" color="error" onClick={() => handleConfirmDelete()}>
                    Delete
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            {fleetGroups?.length > 0 ? (
              <>
                {feedGroupsWithItems?.map((group) => (
                  <GenericToggleGroup
                    setActiveId={(value: string) => mutatActiveChannels(value)}
                    setActiveIds={(values: string[]) => toggleMassUpdate(values)}
                    activeChannels={activeChannels}
                    setDeleteGroup={(value: string) => setConfirmDelete(() => value)}
                    deSelectIds={(values: string[]) => deselectCertainIds(values)}
                    group={group}
                    key={`group-${dynamicKey}-${group.id}`}
                    allowDelete={true}
                  />
                ))}
              </>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 2,
                  p: 4,
                  textAlign: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ fontSize: "1.5rem" }}>My Groups</Box>{" "}
                  <Chip
                    label="beta"
                    size="small"
                    sx={{
                      background: theme.palette.info.main,
                      p: 0,
                      height: "auto",
                    }}
                  />
                </Box>
                <Box>
                  You have no saved groups. To save a group, choose "Save group for future use" while selecting
                  recipients for a Fleet Message.
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
